import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import {
  CheckCircleOutline as ResolveIcon,
  AddCircleOutline as CreateWoIcon,
  DeleteOutline as DeleteIcon,
  SendOutlined as CreateRfsIcon,
  RestartAlt as ReopenIcon,
  LinkOff as UnlinkIcon,
  UndoOutlined as RecallIcon,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { SrTab } from "../../store/serviceReportsSlice";
import accountContext from "../../../../account/accountContext";
import { Roles } from "../../../../account/models/AccountModels";

// ─── Types ─────────────────────────────────────────────────

export type BulkAction =
  | "resolveAll"
  | "createWoForAll"
  | "deleteAll"
  | "createRfsForAll"
  | "escalateToRfs"
  | "unlinkFromWo"
  | "recall"
  | "reopenAll";

// ─── Props ──────────────────────────────────────────────────

interface BulkSelectFloatingBarProps {
  selectedCount: number;
  isPhone: boolean;
  isTablet?: boolean;
  activeTab: SrTab;
  onAction: (action: BulkAction) => void;
  onClear: () => void;
  translate: (key: string) => string;
}

// ─── Button config per tab ─────────────────────────────────

type ButtonVariant = "primary" | "dark" | "danger-outline" | "info" | "muted-outline";

interface ActionButtonConfig {
  action: BulkAction;
  labelKey: string;
  variant: ButtonVariant;
  icon: React.ReactNode;
}

function getButtonsForTab(tab: SrTab, iconSize: number): ActionButtonConfig[] {
  switch (tab) {
    case "untriaged":
      return [
        { action: "createWoForAll", labelKey: "serviceReports.bulk.createWoForAll", variant: "dark", icon: <CreateWoIcon sx={{ fontSize: iconSize }} /> },
        { action: "createRfsForAll", labelKey: "serviceReports.bulk.createRfsForAll", variant: "dark", icon: <CreateRfsIcon sx={{ fontSize: iconSize }} /> },
        { action: "resolveAll", labelKey: "serviceReports.bulk.resolveAll", variant: "primary", icon: <ResolveIcon sx={{ fontSize: iconSize }} /> },
        { action: "deleteAll", labelKey: "serviceReports.bulk.deleteAll", variant: "danger-outline", icon: <DeleteIcon sx={{ fontSize: iconSize }} /> },
      ];
    case "internal":
      return [
        { action: "escalateToRfs", labelKey: "serviceReports.bulk.escalateToRfs", variant: "dark", icon: <CreateRfsIcon sx={{ fontSize: iconSize }} /> },
        { action: "resolveAll", labelKey: "serviceReports.bulk.resolveAll", variant: "primary", icon: <ResolveIcon sx={{ fontSize: iconSize }} /> },
        { action: "unlinkFromWo", labelKey: "serviceReports.bulk.unlinkFromWo", variant: "muted-outline", icon: <UnlinkIcon sx={{ fontSize: iconSize }} /> },
      ];
    case "external":
      return [
        { action: "resolveAll", labelKey: "serviceReports.bulk.resolveAll", variant: "primary", icon: <ResolveIcon sx={{ fontSize: iconSize }} /> },
        { action: "recall", labelKey: "serviceReports.bulk.recall", variant: "muted-outline", icon: <RecallIcon sx={{ fontSize: iconSize }} /> },
      ];
    case "resolved":
      return [
        { action: "reopenAll", labelKey: "serviceReports.bulk.reopenAll", variant: "info", icon: <ReopenIcon sx={{ fontSize: iconSize }} /> },
        { action: "deleteAll", labelKey: "serviceReports.bulk.deleteAll", variant: "danger-outline", icon: <DeleteIcon sx={{ fontSize: iconSize }} /> },
      ];
    default:
      return [];
  }
}

// ─── Component ──────────────────────────────────────────────

const BulkSelectFloatingBar: React.FC<BulkSelectFloatingBarProps> = ({
  selectedCount,
  isPhone,
  isTablet,
  activeTab,
  onAction,
  onClear,
  translate,
}) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  // Inject keyframes
  useEffect(() => {
    const id = "bulk-floating-bar-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes bulkBarSlideUp {
          0% { transform: translateX(-50%) translateY(100%); opacity: 0; }
          100% { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        @keyframes bulkBarSlideDown {
          0% { transform: translateX(-50%) translateY(0); opacity: 1; }
          100% { transform: translateX(-50%) translateY(100%); opacity: 0; }
        }
        @keyframes bulkBarSlideUpPhone {
          0% { transform: translateY(100%); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes bulkBarSlideDownPhone {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Animate in/out
  useEffect(() => {
    if (selectedCount > 0) {
      setVisible(true);
      setExiting(false);
    } else if (visible) {
      setExiting(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [selectedCount, visible]);

  const canManage = accountContext.user?.role !== Roles.WORKER;

  if (!visible) return null;

  const compact = isPhone;
  const iconSize = compact ? 14 : 15;
  // Filter out "deleteAll" action for non-Admin users (hard delete = Admin only)
  const buttons = getButtonsForTab(activeTab, iconSize).filter(
    (btn) => btn.action !== "deleteAll" || canManage
  );

  // Phone: full-width docked bar
  if (isPhone) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1350,
          animation: exiting
            ? "bulkBarSlideDownPhone 0.25s ease forwards"
            : "bulkBarSlideUpPhone 0.3s ease",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderTop: "3px solid #D4572B",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.10)",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <CountPill count={selectedCount} translate={translate} />
          <Box sx={{ flex: 1 }} />
          {buttons.map((btn) => (
            <ActionButton
              key={btn.action}
              label={translate(btn.labelKey)}
              variant={btn.variant}
              icon={btn.icon}
              onClick={() => onAction(btn.action)}
              compact
            />
          ))}
          <Box
            onClick={onClear}
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 500,
              color: "#8C9099",
              cursor: "pointer",
              padding: "4px 8px",
              transition: "color 0.15s ease",
              "&:hover": { color: "#4A4F57" },
            }}
          >
            {translate("serviceReports.bulk.clear")}
          </Box>
        </Box>
      </Box>
    );
  }

  // Tablet: full-width docked bar with desktop-sized buttons
  if (isTablet) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1350,
          animation: exiting
            ? "bulkBarSlideDownPhone 0.25s ease forwards"
            : "bulkBarSlideUpPhone 0.3s ease",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderTop: "3px solid #D4572B",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.10)",
            padding: "12px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <CountPill count={selectedCount} translate={translate} />
          {buttons.map((btn) => (
            <ActionButton
              key={btn.action}
              label={translate(btn.labelKey)}
              variant={btn.variant}
              icon={btn.icon}
              onClick={() => onAction(btn.action)}
            />
          ))}
          <Box
            onClick={onClear}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 500,
              color: "#8C9099",
              cursor: "pointer",
              padding: "6px 10px",
              borderRadius: "8px",
              transition: "all 0.15s ease",
              "&:hover": { color: "#4A4F57", backgroundColor: "#F7F5F0" },
            }}
          >
            {translate("serviceReports.bulk.clear")}
          </Box>
        </Box>
      </Box>
    );
  }

  // Desktop: centered floating bar
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: "24px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1350,
        animation: exiting
          ? "bulkBarSlideDown 0.25s ease forwards"
          : "bulkBarSlideUp 0.3s ease",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          borderTop: "3px solid #D4572B",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          whiteSpace: "nowrap",
        }}
      >
        <CountPill count={selectedCount} translate={translate} />
        {buttons.map((btn) => (
          <ActionButton
            key={btn.action}
            label={translate(btn.labelKey)}
            variant={btn.variant}
            icon={btn.icon}
            onClick={() => onAction(btn.action)}
          />
        ))}
        <Box
          onClick={onClear}
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 500,
            color: "#8C9099",
            cursor: "pointer",
            padding: "6px 10px",
            borderRadius: "8px",
            transition: "all 0.15s ease",
            "&:hover": { color: "#4A4F57", backgroundColor: "#F7F5F0" },
          }}
        >
          {translate("serviceReports.bulk.clear")}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Sub-components ─────────────────────────────────────────

/** Cream/warm pill showing count */
const CountPill: React.FC<{ count: number; translate: (k: string) => string }> = ({
  count,
  translate,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      backgroundColor: "#FDF5F2",
      borderRadius: "20px",
      padding: "6px 14px",
      border: "1px solid #F0E6E0",
    }}
  >
    <Box
      sx={{
        fontFamily: dt.fontMono,
        fontSize: "13px",
        fontWeight: 700,
        color: "#D4572B",
      }}
    >
      {count}
    </Box>
    <Box
      sx={{
        fontFamily: dt.font,
        fontSize: "13px",
        fontWeight: 500,
        color: "#8C7A70",
      }}
    >
      {translate("serviceReports.bulk.selected")}
    </Box>
  </Box>
);

/** Action button with variants: primary (terracotta), dark (charcoal), danger-outline, info (blue), muted-outline */
const ActionButton: React.FC<{
  label: string;
  variant: ButtonVariant;
  icon?: React.ReactNode;
  onClick: () => void;
  compact?: boolean;
}> = ({ label, variant, icon, onClick, compact }) => {
  const styles: Record<ButtonVariant, {
    color: string;
    background: string;
    border: string;
    hoverBg: string;
    shadow: string;
  }> = {
    primary: {
      color: "#FFFFFF",
      background: "linear-gradient(135deg, #D4572B, #B8451F)",
      border: "none",
      hoverBg: "linear-gradient(135deg, #B8451F, #D4572B)",
      shadow: "0 2px 8px rgba(212,87,43,0.25)",
    },
    dark: {
      color: "#FFFFFF",
      background: "#0C1117",
      border: "none",
      hoverBg: "#1A1F28",
      shadow: "0 2px 8px rgba(12,17,23,0.20)",
    },
    "danger-outline": {
      color: "#D4572B",
      background: "transparent",
      border: "1.5px solid #D4572B",
      hoverBg: "rgba(212,87,43,0.06)",
      shadow: "none",
    },
    info: {
      color: "#FFFFFF",
      background: "linear-gradient(135deg, #3B82F6, #2563EB)",
      border: "none",
      hoverBg: "linear-gradient(135deg, #2563EB, #3B82F6)",
      shadow: "0 2px 8px rgba(59,130,246,0.25)",
    },
    "muted-outline": {
      color: "#6B7280",
      background: "transparent",
      border: "1.5px solid #D1CCC4",
      hoverBg: "rgba(107,114,128,0.06)",
      shadow: "none",
    },
  };

  const s = styles[variant];

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontFamily: dt.font,
        fontSize: compact ? "12px" : "13px",
        fontWeight: 600,
        color: s.color,
        background: s.background,
        border: s.border,
        borderRadius: "24px",
        padding: compact ? "6px 14px" : "8px 18px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: s.shadow,
        userSelect: "none",
        "&:hover": {
          background: s.hoverBg,
          boxShadow: variant === "danger-outline" || variant === "muted-outline" ? "none" : s.shadow,
        },
      }}
    >
      {icon}
      {label}
    </Box>
  );
};

export default BulkSelectFloatingBar;
